import { apiDownload, apiRequest, type IDownload } from '@/domains/api';
import type { IListCounts, IListFacets } from './lists-contracts';
import type {
  IMetricsCountsRequest,
  IMetricsFacetsRequest,
  IMetricsFirms,
  IMetricsMailMergeRequest,
  IMetricsFirmWrite,
  IMetricsRows,
  IMetricsRowsRequest,
  IMetricsUpdates
} from './metrics-updates-contracts';
import { isObject } from './rankings-api';

/**
 * survey-api's Metrics updates (SR-315) — the sole reader of `/api/v1/admin/metrics-updates*`:
 * the survey's collection round and its editions, the two lists' rows, and the firms' status in
 * the round.
 *
 * **The rows are survey-api's to choose.** The request names the edition, the tab and the
 * filters; survey-api composes the criteria — eligibility, the firm's status, the list's template,
 * the threshold, whether the figures were received — and answers the Lists read's rows. No
 * criterion is built here, so the page and survey-api cannot disagree about who owes figures.
 * A 2xx of another shape is a failed read.
 */

const BASE = '/api/v1/admin/metrics-updates';

function surveyPath(surveyCode: string): string {
  return `${BASE}/${encodeURIComponent(surveyCode)}`;
}

function updatesOf(answer: unknown): IMetricsUpdates {
  if (!isObject(answer) || !Array.isArray(answer.editions) || !Array.isArray(answer.figureSeries)) {
    throw new Error('The answer held no metrics round');
  }
  return answer as unknown as IMetricsUpdates;
}

function rowsOf(answer: unknown): IMetricsRows {
  if (!isObject(answer) || !Array.isArray(answer.rows) || !isObject(answer.counts)) {
    throw new Error('The answer held no metrics rows');
  }
  return answer as unknown as IMetricsRows;
}

function facetsOf(answer: unknown): IListFacets {
  if (!isObject(answer) || !Array.isArray(answer.values)) {
    throw new Error('The answer held no values');
  }
  return answer as unknown as IListFacets;
}

function firmsOf(answer: unknown): IMetricsFirms {
  if (!isObject(answer) || !Array.isArray(answer.firms)) {
    throw new Error('The answer held no firms');
  }
  return answer as unknown as IMetricsFirms;
}

export const metricsUpdatesApi = {
  /** The survey's round, its editions with their thresholds, the figure series a threshold may
   *  name and the survey's preset (`responses.browse`). 400 `invalid-query` for no such survey. */
  round(surveyCode: string, signal?: AbortSignal): Promise<IMetricsUpdates> {
    return apiRequest<unknown>(surveyPath(surveyCode), { signal }).then(updatesOf);
  },

  /** A page of one list and both lists' counts (`responses.browse`). 404 for no edition at the
   *  address; 409 `no-round`, `threshold-missing` or `template-untranslatable` (its `detail`
   *  naming the condition); 400 at `firm`. */
  rows(surveyCode: string, body: IMetricsRowsRequest, signal?: AbortSignal): Promise<IMetricsRows> {
    return apiRequest<unknown>(`${surveyPath(surveyCode)}/rows`, {
      method: 'POST',
      body,
      signal
    }).then(rowsOf);
  },

  /** A column's funnel values under the list's own conditions and `criteria` (`responses.browse`),
   *  as `/admin/lists/facets` answers them; 400 at `criteria` or `search`. */
  facets(surveyCode: string, body: IMetricsFacetsRequest, signal?: AbortSignal): Promise<IListFacets> {
    return apiRequest<unknown>(`${surveyPath(surveyCode)}/facets`, { method: 'POST', body, signal }).then(facetsOf);
  },

  /** Each key's count under the scoping AND its criteria, on its own tab where it names one. */
  counts(surveyCode: string, body: IMetricsCountsRequest, signal?: AbortSignal): Promise<IListCounts> {
    return apiRequest<unknown>(`${surveyPath(surveyCode)}/counts`, { method: 'POST', body, signal }).then((answer) => {
      if (!isObject(answer) || !isObject(answer.counts)) throw new Error('The answer held no counts');
      return answer as unknown as IListCounts;
    });
  },

  /** The list as a CSV for a mail merge (WF-10 step 2, until SR-335's templated outreach) — the
   *  ticked rows, else every row under the filters — recording a request per row where `record`
   *  (`internal_fields.write`). */
  mailMerge(surveyCode: string, body: IMetricsMailMergeRequest): Promise<IDownload> {
    return apiDownload(`${surveyPath(surveyCode)}/mail-merge`, { method: 'POST', body });
  },

  /** The firms the survey's rows name, most advisors first, each with its status in the round. */
  firms(surveyCode: string, signal?: AbortSignal): Promise<IMetricsFirms> {
    return apiRequest<unknown>(`${surveyPath(surveyCode)}/firms`, { signal }).then(firmsOf);
  },

  /** Writes firms' statuses in the round (`responses.annotate`) and answers the firms read
   *  again. 409 `no-round`; 400 `problems[]` at `items[i]`. */
  setFirms(surveyCode: string, items: IMetricsFirmWrite[]): Promise<IMetricsFirms> {
    return apiRequest<unknown>(`${surveyPath(surveyCode)}/firms`, {
      method: 'PUT',
      body: { items }
    }).then(firmsOf);
  }
};
